/**
 * 스트리밍 마크다운 — 구 `@comark/nuxt` 의 대체(F §10-3).
 *
 * 스트리밍 중에는 문법이 **항상 미완결 상태**를 거친다: 코드펜스가 열린 채, 표가 헤더만,
 * 굵게가 `**` 하나만. 그대로 렌더하면 화면이 매 토큰마다 요동친다("깨졌다"로 보인다).
 * 구 Comark 가 해결하던 것이 이것이고, 여기서는 **미완결 코드펜스만 임시로 닫아** 렌더한다 —
 * 나머지(강조·표)는 remark 가 부분 입력에서도 안정적으로 처리한다.
 */
/* eslint-disable react-refresh/only-export-components --
 * `closeDanglingFence` 는 **테스트가 직접 부르는 순수 함수**다(Markdown.test.ts). 컴포넌트에
 * 숨기면 스트리밍 중 미완결 코드펜스 처리를 유닛으로 고정할 수 없다 — 그 검증이 이 파일의
 * 존재 이유라 함께 둔다. */
import { lazy, memo, Suspense } from 'react'

/** 열린 채 끝난 코드펜스를 임시로 닫는다(원문은 건드리지 않는다 — 렌더 입력만). */
export function closeDanglingFence(markdown: string): string {
  const fences = markdown.match(/^```/gm)?.length ?? 0
  return fences % 2 === 1 ? `${markdown}\n\`\`\`` : markdown
}

export const Markdown = memo(function Markdown({ text }: { text: string }) {
  const body = closeDanglingFence(text)
  return (
    <div className="prose-chat">
      <Suspense fallback={<p className="whitespace-pre-wrap">{text}</p>}>
        <MarkdownBody text={body} />
      </Suspense>
    </div>
  )
})

/**
 * 렌더러는 **지연 로드**한다. 셸이 그려지는 시점에는 마크다운이 필요 없고(첫 응답 토큰이 오기
 * 전까지 쓰이지 않는다), 이 의존이 초기 번들의 가장 큰 비-프레임워크 덩어리였다(150KB).
 * remark 플러그인도 함께 불러야 왕복이 하나다.
 */
const MarkdownBody = lazy(async () => {
  const [{ default: Renderer }, { default: remarkGfm }] = await Promise.all([
    import('react-markdown'),
    import('remark-gfm'),
  ])
  return {
    default: ({ text }: { text: string }) => (
      <Renderer remarkPlugins={[remarkGfm]}>{text}</Renderer>
    ),
  }
})
